/**
 * CalendarDayDetailModal — Vue détaillée d'une tenue portée à une date.
 *
 * Affiche : date + occasion + météo + photo (manuelle ou items collage)
 * + liste des items + notes + bouton supprimer.
 *
 * Remplace l'ancien showAppAlert qui ne montrait qu'un texte.
 *
 * v1.12.236
 */

import React, { useState } from 'react';
import {
  View, Text, TouchableOpacity, ScrollView, StyleSheet, Image,
  ActivityIndicator, Alert, useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AppModal from './AppModal';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { deleteEntry, getEntryDetail } from '../services/CalendarService';
import { getOccasionDisplay, getWeatherIcon, getCalendarSnapshot } from '../services/CalendarAgentService';
import { getWardrobe } from '../services/DatabaseService';
import { showAppAlert } from './AppDialog';

const GOLD = '#D4AF72';
const BORDER = '#EAE6E0';

export default function CalendarDayDetailModal({ visible, entry, dateLabel, onClose, onDeleted }) {
  const { user, getValidToken } = useAuth();
  const { t, lang } = useI18n();
  const insets = useSafeAreaInsets();
  const { height: winH } = useWindowDimensions();
  const [deleting, setDeleting] = useState(false);
  // v1.12.238 — snapshot local (items complets sauvegardés à l'ajout)
  const [snapshot, setSnapshot] = useState(null);
  // v1.12.241 — détail entry (calendar_items refs) + wardrobe pour enrichir items
  const [detailItems, setDetailItems] = useState(null);
  const [wardrobeMap, setWardrobeMap] = useState(null);
  React.useEffect(() => {
    if (!visible || !entry?.date || !user?.id) return;
    let cancelled = false;
    // 1) snapshot local (rapide)
    getCalendarSnapshot({ userId: user.id, date: entry.date })
      .then(s => { if (!cancelled) setSnapshot(s); })
      .catch(() => setSnapshot(null));
    // 2) détail serveur (calendar_items references)
    if (entry.id) {
      const tokenP = getValidToken ? getValidToken() : Promise.resolve(user.accessToken);
      tokenP.then(token => getEntryDetail({ id: entry.id, authToken: token }))
        .then(r => { if (!cancelled) setDetailItems(r?.data?.items || r?.items || []); })
        .catch(() => setDetailItems([]));
    }
    // 3) wardrobe map pour enrichir item_id → { name, image, brand }
    const tokenP2 = getValidToken ? getValidToken() : Promise.resolve(user.accessToken);
    tokenP2.then(token => getWardrobe(user.id, token))
      .then(w => {
        if (cancelled) return;
        const map = new Map();
        for (const it of (w || [])) map.set(String(it.id), it);
        setWardrobeMap(map);
      })
      .catch(() => setWardrobeMap(new Map()));
    return () => { cancelled = true; };
  }, [visible, entry?.date, entry?.id, user?.id, getValidToken]);

  if (!visible || !entry) return null;

  const cardMaxHeight = Math.min(720, winH * 0.92);
  const headerH = 70;
  const footerH = 80;
  const scrollMax = Math.max(220, cardMaxHeight - headerH - footerH);

  const occDisp = getOccasionDisplay(entry.occasion);
  const weatherIcon = getWeatherIcon(entry.weather_condition || entry.weather?.condition);
  const tempC = entry.weather_temp_celsius != null ? entry.weather_temp_celsius : entry.weather?.temp_celsius;
  // v1.12.238 — photo : snapshot.photo (manuel) > entry.user_photo_url > entry.composite
  const photoUrl = snapshot?.photo || entry.user_photo_url || entry.composite_image_url;
  // v1.12.241 — Sources items, dans l'ordre de préférence :
  //   1. snapshot.items (full data : name, image_url, brand, category)
  //   2. detailItems (server refs) enrichis via wardrobeMap (lookup item_id → wardrobe)
  //   3. entry.items (déjà fournis par listMonth, rare)
  const rawEntryItems = entry.items || [];
  const hasFullEntryData = rawEntryItems.length > 0 && rawEntryItems.some(it => it.image_url || it.image || it.name);
  let items = [];
  if (snapshot?.items?.length > 0) {
    items = snapshot.items;
  } else if (hasFullEntryData) {
    items = rawEntryItems;
  } else if (detailItems && wardrobeMap) {
    // Enrichir refs serveur via wardrobe
    items = detailItems.map(ref => {
      const w = wardrobeMap.get(String(ref.item_id));
      if (w) {
        return {
          id: w.id, name: w.name, brand: w.brand,
          image_url: w.image_url || w.image,
          category: ref.category || w.category,
        };
      }
      return { id: ref.item_id, name: null, image_url: null, category: ref.category };
    });
  } else if (detailItems && detailItems.length > 0) {
    // Wardrobe pas encore chargée : fallback sans images
    items = detailItems.map(ref => ({ id: ref.item_id, category: ref.category, name: null }));
  }
  // Source : prend snapshot si plus précis (manual avec photo, looks_ia, etc.)
  const effectiveSource = entry.source || snapshot?.source;
  const isManualPhoto = effectiveSource === 'manual';
  const isLoadingItems = !snapshot && !hasFullEntryData && (detailItems == null || wardrobeMap == null);

  async function handleDelete() {
    // v1.12.245 — showAppAlert (cross-platform : web Alert.alert ne déclenche
    // que OK pour les multi-boutons → bouton Supprimer ne marchait pas sur web)
    const deleteMsg = lang === 'en'
      ? `Would you like to delete the outfit from ${dateLabel}? This action is final.`
      : `Voulez-vous supprimer la tenue du ${dateLabel} ? Cette action est définitive.`;
    showAppAlert(
      t('delete_outfit_title'),
      deleteMsg,
      [
        { text: t('cancel'), style: 'cancel' },
        {
          text: t('delete'), style: 'destructive',
          onPress: async () => {
            setDeleting(true);
            try {
              const token = getValidToken ? await getValidToken() : user.accessToken;
              await deleteEntry({ id: entry.id, authToken: token });
              onDeleted && onDeleted();
              onClose && onClose();
            } catch (e) {
              showAppAlert(t('error'), e?.message || t('cannot_delete'), [{ text: 'OK' }]);
            } finally {
              setDeleting(false);
            }
          },
        },
      ],
    );
  }

  return (
    <AppModal visible transparent animationType="fade" onRequestClose={onClose} webBackdropClosable webMaxWidth={520}>
      <View style={[s.card, { marginTop: Math.max(insets.top, 24), maxHeight: cardMaxHeight }]}>
        {/* Header */}
        <View style={s.header}>
          <View style={{ flex: 1 }}>
            <Text style={s.title}>{t('outfit_worn')}</Text>
            <Text style={s.sub}>{dateLabel}</Text>
          </View>
          <TouchableOpacity onPress={onClose} style={s.close} hitSlop={{top:10,right:10,bottom:10,left:10}}>
            <Text style={s.closeTxt}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={{ maxHeight: scrollMax }} contentContainerStyle={{ padding: 18, paddingTop: 12 }}>
          {/* Chips occasion + météo */}
          <View style={s.metaRow}>
            {entry.occasion && (
              <View style={[s.chip, { backgroundColor: occDisp.bg }]}>
                <Text style={{ fontSize: 14 }}>{occDisp.emoji}</Text>
                <Text style={[s.chipTxt, { color: occDisp.color }]}>{occDisp.label}</Text>
              </View>
            )}
            {weatherIcon && (
              <View style={s.chipWeather}>
                <Text style={{ fontSize: 14 }}>{weatherIcon}</Text>
                {tempC != null && <Text style={s.chipTxt}>{tempC}°C</Text>}
              </View>
            )}
            {entry.rating && (
              <View style={s.chipRating}>
                <Text style={{ fontSize: 12, color: '#FFD700' }}>★</Text>
                <Text style={[s.chipTxt, { color: '#1A1814' }]}>{entry.rating}/5</Text>
              </View>
            )}
          </View>

          {/* Photo principale (user_photo ou composite) */}
          {photoUrl ? (
            <View style={s.photoFrame}>
              <Image source={{ uri: photoUrl }} style={s.photo} resizeMode="contain" />
            </View>
          ) : items.length > 0 ? (
            // Collage 2x2 max
            <View style={s.collage}>
              {items.slice(0, 4).map((it, i) => (
                <View key={i} style={s.collageCell}>
                  {(it.image_url || it.image)
                    ? <Image source={{ uri: it.image_url || it.image }} style={{ width: '100%', height: '100%' }} resizeMode="contain" />
                    : <Text style={{ fontSize: 24 }}>{getCatEmoji(it.category)}</Text>}
                </View>
              ))}
            </View>
          ) : (
            <View style={s.placeholder}>
              <Text style={{ fontSize: 56 }}>👗</Text>
            </View>
          )}

          {/* Liste items */}
          {items.length > 0 && (
            <View style={s.section}>
              <Text style={s.sectionLabel}>{t('pieces')} ({items.length})</Text>
              {items.map((it, i) => (
                <View key={i} style={s.itemRow}>
                  <View style={s.itemThumb}>
                    {(it.image_url || it.image)
                      ? <Image source={{ uri: it.image_url || it.image }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                      : <Text style={{ fontSize: 16 }}>{getCatEmoji(it.category)}</Text>}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={s.itemName} numberOfLines={1}>{it.name || it.brand || `${lang === 'en' ? 'Item' : 'Article'} #${i + 1}`}</Text>
                    {it.category && <Text style={s.itemCat}>{it.category}</Text>}
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Notes */}
          {entry.notes && (
            <View style={s.section}>
              <Text style={s.sectionLabel}>{t('notes')}</Text>
              <Text style={s.notes}>{entry.notes}</Text>
            </View>
          )}

          {/* Lieu */}
          {entry.location && (
            <View style={s.section}>
              <Text style={s.sectionLabel}>{t('location')}</Text>
              <Text style={s.notes}>📍 {entry.location}</Text>
            </View>
          )}

          {/* Source */}
          {entry.source && (
            <Text style={s.sourceTxt}>
              {t('added_from')} {sourceLabel(entry.source, lang)}
            </Text>
          )}
        </ScrollView>

        {/* Footer avec bouton supprimer */}
        <View style={s.footer}>
          <TouchableOpacity
            style={[s.btnDelete, deleting && { opacity: 0.6 }]}
            onPress={handleDelete}
            disabled={deleting}
            activeOpacity={0.85}>
            {deleting
              ? <ActivityIndicator size="small" color="#FFFFFF" />
              : <Text style={s.btnDeleteTxt}>{t('delete_this_outfit')}</Text>}
          </TouchableOpacity>
        </View>
      </View>
    </AppModal>
  );
}

function getCatEmoji(cat) {
  const c = String(cat || '').toLowerCase();
  if (c.includes('robe') || c === 'dress') return '👗';
  if (c.includes('haut') || c === 'top') return '👚';
  if (c.includes('bas') || c === 'bottom') return '👖';
  if (c.includes('chaussure') || c === 'shoes') return '👟';
  if (c.includes('sac') || c === 'bag') return '👜';
  if (c.includes('manteau')) return '🧥';
  if (c.includes('veste')) return '🧥';
  if (c.includes('bijou')) return '💍';
  return '✨';
}

function sourceLabel(source, lang) {
  const en = lang === 'en';
  switch (source) {
    case 'tenue_du_jour': return en ? 'Outfit of the day ✨' : 'Tenue du jour ✨';
    case 'mes_looks': return en ? 'My Looks' : 'Mes Looks';
    case 'looks_ia': return en ? 'AI Looks' : 'Looks IA';
    case 'manual': return en ? 'Manual photo 📸' : 'Photo manuelle 📸';
    default: return source;
  }
}

const s = StyleSheet.create({
  card: {
    width: '100%', backgroundColor: '#FFFFFF',
    borderRadius: 18, overflow: 'hidden',
  },
  header: {
    flexDirection: 'row', alignItems: 'center',
    paddingTop: 18, paddingHorizontal: 18, paddingBottom: 12,
    borderBottomWidth: 1, borderBottomColor: BORDER,
  },
  title: { fontSize: 16, fontWeight: '700', color: '#1A1814' },
  sub:   { fontSize: 12, color: '#7A7268', marginTop: 2 },
  close: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  closeTxt: { fontSize: 18, color: '#7A7268' },

  metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 14 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14 },
  chipWeather: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14, backgroundColor: '#E8F1FB' },
  chipRating:  { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14, backgroundColor: '#FFF7E0', borderWidth: 1, borderColor: GOLD },
  chipTxt: { fontSize: 11, fontWeight: '700', letterSpacing: 0.3, color: '#1A1814' },

  photoFrame: {
    width: '100%', aspectRatio: 4 / 5, maxHeight: 360,
    backgroundColor: '#FBF7EE',
    borderRadius: 14, overflow: 'hidden',
    alignItems: 'center', justifyContent: 'center',
    marginBottom: 14,
  },
  photo: { width: '100%', height: '100%' },
  collage: {
    width: '100%', aspectRatio: 1,
    flexDirection: 'row', flexWrap: 'wrap',
    backgroundColor: '#FBF7EE', borderRadius: 14, overflow: 'hidden',
    marginBottom: 14,
  },
  collageCell: { width: '50%', height: '50%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FBF7EE', padding: 4 },
  placeholder: {
    width: '100%', aspectRatio: 1,
    backgroundColor: '#FBF7EE', borderRadius: 14,
    alignItems: 'center', justifyContent: 'center', marginBottom: 14,
  },

  section: { marginTop: 6 },
  sectionLabel: { fontSize: 10, fontWeight: '700', color: '#7A7268', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 },

  itemRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 6,
    borderBottomWidth: 1, borderBottomColor: '#F5F2ED',
  },
  itemThumb: {
    width: 40, height: 50, borderRadius: 6,
    backgroundColor: '#F5F2ED', overflow: 'hidden',
    alignItems: 'center', justifyContent: 'center',
  },
  itemName: { fontSize: 13, color: '#1A1814', fontWeight: '500' },
  itemCat:  { fontSize: 10, color: '#7A7268', marginTop: 2, letterSpacing: 0.4, textTransform: 'uppercase' },

  notes: { fontSize: 13, color: '#1A1814', lineHeight: 19 },
  sourceTxt: { fontSize: 10, color: '#A89E94', marginTop: 16, fontStyle: 'italic', textAlign: 'center' },

  footer: {
    paddingHorizontal: 18, paddingTop: 12, paddingBottom: 18,
    borderTopWidth: 1, borderTopColor: BORDER,
    backgroundColor: '#FFFFFF',
  },
  btnDelete: {
    backgroundColor: '#C62828',
    paddingVertical: 14, borderRadius: 100,
    alignItems: 'center',
  },
  btnDeleteTxt: { fontSize: 12, fontWeight: '700', color: '#FFFFFF', letterSpacing: 0.8 },
});
